import React, { useState } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import { loadUser } from '../actions/registerUser';
import './EmailChangeControl.css';

const CODE_LENGTH = 6;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// POST /update_user's diverted email-change errors come back as
// { errors: '<string>' } (not the { errors: [...] } array shape other
// forms in this app use), while /email_change/verify uses { msg }.
// Covers both instead of guessing one.
const errorMessage = (err, fallback) => {
  const data = err.response && err.response.data;
  if (!data) return fallback;
  if (typeof data.msg === 'string') return data.msg;
  if (typeof data.errors === 'string') return data.errors;
  return fallback;
};

// Post-activation email changes only -- rendered by EditProfile.js in
// place of the plain, directly-editable input once the account is
// activated (see EditProfile.js's own comment on why). Deliberately
// self-contained: it never writes into EditProfile's own `User` local
// state, so a change here can never accidentally ride along with (or
// get overwritten by) an unrelated bio/social/password submit through
// the big form's single submit button, and vice versa.
const EmailChangeControl = ({ currentEmail, loadUser }) => {
  const [mode, setMode] = useState('view'); // 'view' | 'request' | 'code' | 'success'
  const [newEmail, setNewEmail] = useState('');
  const [code, setCode] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');

  const startRequest = () => {
    setMode('request');
    setNewEmail('');
    setError('');
  };

  const cancel = () => {
    setMode('view');
    setNewEmail('');
    setCode('');
    setError('');
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    // Nested inside EditProfile.js's big Settings-tab <form> -- see
    // UsernameChangeControl.js's identical comment on why
    // stopPropagation() (not just preventDefault()) is required here to
    // stop this submit from also bubbling into that outer form's own
    // onSubmit, which would fire a second, stray POST /update_user.
    e.stopPropagation();
    const trimmed = newEmail.trim();

    if (trimmed.toLowerCase() === currentEmail.trim().toLowerCase()) {
      setError('Enter a different email address.');
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }

    setRequesting(true);
    setError('');
    try {
      const res = await axios.post('/api/users/update_user', { email: trimmed });
      setPendingEmail(res.data.pendingEmail);
      setCode('');
      setMode('code');
    } catch (err) {
      setError(errorMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setRequesting(false);
    }
  };

  const resend = async () => {
    setResending(true);
    setError('');
    try {
      const res = await axios.post('/api/users/update_user', { email: pendingEmail });
      setPendingEmail(res.data.pendingEmail);
      setCode('');
    } catch (err) {
      setError(errorMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setResending(false);
    }
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    // Same reason as submitRequest above.
    e.stopPropagation();
    setVerifying(true);
    setError('');
    try {
      await axios.post('/api/users/email_change/verify', { code });
      setMode('success');
      loadUser();
    } catch (err) {
      setError(errorMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setVerifying(false);
    }
  };

  if (mode === 'view') {
    return (
      <div className="form-con">
        <span>Login Email</span>
        <div className="email-change__current">
          <input type="email" value={currentEmail} disabled />
          <button type="button" className="email-change__link" onClick={startRequest}>
            Change
          </button>
        </div>
      </div>
    );
  }

  if (mode === 'request') {
    return (
      <div className="form-con">
        <span>Login Email</span>
        <form onSubmit={submitRequest}>
          <p className="email-change__disclosure">
            Changing your email requires confirming it's yours. Current: {currentEmail}
          </p>
          {error && <p className="email-change__error">{error}</p>}
          <input
            type="email"
            className="email-change__input"
            placeholder="New email address"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            autoFocus
          />
          <div className="email-change__actions">
            <button type="submit" className="email-change__submit" disabled={requesting}>
              {requesting ? 'Sending…' : 'Send code'}
            </button>
            <button type="button" className="email-change__cancel" onClick={cancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (mode === 'code') {
    return (
      <div className="form-con">
        <span>Login Email</span>
        <form onSubmit={submitVerify}>
          <p className="email-change__disclosure">
            We sent a {CODE_LENGTH}-digit code to {pendingEmail}. Enter it below to confirm
            the change.
          </p>
          {error && <p className="email-change__error">{error}</p>}
          <input
            type="text"
            inputMode="numeric"
            maxLength={CODE_LENGTH}
            className="email-change__input email-change__input--code"
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            autoFocus
          />
          <div className="email-change__actions">
            <button
              type="submit"
              className="email-change__submit"
              disabled={verifying || code.length !== CODE_LENGTH}
            >
              {verifying ? 'Verifying…' : 'Verify'}
            </button>
            <button type="button" className="email-change__cancel" onClick={cancel}>
              Cancel
            </button>
          </div>
          <button
            type="button"
            className="email-change__resend"
            onClick={resend}
            disabled={resending}
          >
            {resending ? 'Sending…' : "Didn't get it? Resend code"}
          </button>
        </form>
      </div>
    );
  }

  // mode === 'success'
  return (
    <div className="form-con">
      <span>Login Email</span>
      <p className="email-change__success">Your email has been updated.</p>
      <button type="button" className="email-change__link" onClick={cancel}>
        Done
      </button>
    </div>
  );
};

export default connect(null, { loadUser })(EmailChangeControl);
